import { useState } from "react";
import { NavLink, useLocation } from "react-router-dom";
import { useAuth } from "@/components/feature/AuthProvider";
import { useClub } from "@/hooks/useClub";

interface TabItem {
  label: string;
  to: string;
  icon: string;
}

const TABS: TabItem[] = [
  { label: "명함", to: "/cards", icon: "ri-id-card-line" },
  { label: "비즈맵", to: "/bizmap", icon: "ri-map-pin-2-line" },
  { label: "일정", to: "/calendar", icon: "ri-calendar-check-line" },
  { label: "자유글", to: "/free", icon: "ri-chat-3-line" },
];

const MORE_ITEMS = [
  { label: "메인", to: "/", icon: "ri-home-5-line" },
  { label: "클럽소개", to: "/about", icon: "ri-information-line" },
  { label: "조직도", to: "/org", icon: "ri-node-tree" },
  { label: "규정", to: "/rules", icon: "ri-file-text-line" },
  { label: "회원", to: "/members", icon: "ri-team-line" },
  { label: "직업홍보", to: "/promo", icon: "ri-megaphone-line" },
  { label: "내가쏜다", to: "/treat", icon: "ri-hand-heart-line" },
  { label: "가위바위보", to: "/rps", icon: "ri-gamepad-line" },
  { label: "줄다리기", to: "/tug", icon: "ri-drag-move-2-line" },
  { label: "게임", to: "/games", icon: "ri-trophy-line" },
  { label: "걸음 랭킹", to: "/steps", icon: "ri-footprint-line" },
  { label: "투표·설문", to: "/vote", icon: "ri-bar-chart-box-line" },
  { label: "일정", to: "/calendar", icon: "ri-calendar-check-line" },
  { label: "사다리게임", to: "/ladder", icon: "ri-gift-2-line" },
  { label: "활동갤러리", to: "/gallery", icon: "ri-image-line" },
  { label: "공지사항", to: "/notices", icon: "ri-notification-3-line" },
  { label: "문의하기", to: "/contact", icon: "ri-customer-service-2-line" },
];

// 모바일 하단 고정 탭바 (5개)
export default function MobileTabBar() {
  const { member, openLogin, logout } = useAuth();
  const { club } = useClub();
  const location = useLocation();
  const [moreOpen, setMoreOpen] = useState(false);

  // sections.step 이 꺼져 있으면 «걸음 랭킹» 항목을 숨긴다.
  const stepOn = !club?.sections || Number(club.sections.step ?? 1) !== 0;
  const moreItems = stepOn
    ? MORE_ITEMS
    : MORE_ITEMS.filter((item) => item.to !== "/steps");

  const tabClass = (active: boolean) =>
    `flex flex-1 flex-col items-center justify-center gap-0.5 py-2 text-[11px] transition-colors ${
      active ? "text-primary-700 font-semibold" : "text-foreground-500"
    }`;

  return (
    <>
      <nav
        className="mobile-tabbar fixed inset-x-0 bottom-0 z-[150] flex border-t border-background-200 bg-background-50/95 backdrop-blur xl:hidden"
        aria-label="모바일 하단 메뉴"
      >
        {TABS.map((tab) => (
          <NavLink
            key={tab.to}
            to={tab.to}
            className={({ isActive }) =>
              tabClass(isActive || location.pathname.startsWith(`${tab.to}/`))
            }
          >
            <i className={`${tab.icon} text-xl`} aria-hidden="true"></i>
            {tab.label}
          </NavLink>
        ))}
        <button
          type="button"
          onClick={() => setMoreOpen(true)}
          className={tabClass(false)}
          aria-haspopup="dialog"
        >
          <i className="ri-menu-2-line text-xl" aria-hidden="true"></i>
          더보기
        </button>
      </nav>

      {moreOpen && (
        <div className="fixed inset-0 z-[200] xl:hidden" role="dialog" aria-modal="true">
          <div
            className="absolute inset-0 bg-primary-950/50"
            onClick={() => setMoreOpen(false)}
            aria-hidden="true"
          ></div>
          <div className="sheet-safe absolute inset-x-0 bottom-0 rounded-t-2xl bg-background-50 p-5">
            <div className="mx-auto mb-4 h-1.5 w-10 rounded-full bg-background-300" aria-hidden="true"></div>

            <div className="mb-4 flex items-center justify-between">
              {member ? (
                <div className="flex items-center gap-2.5">
                  <span className="w-9 h-9 flex items-center justify-center rounded-full bg-primary-500 text-background-50 font-bold">
                    {(member.name || "회").slice(0, 1)}
                  </span>
                  <span className="font-semibold text-foreground-900">
                    {member.name}님
                  </span>
                </div>
              ) : (
                <span className="text-sm text-foreground-600">
                  로그인하고 더 많은 기능을 이용하세요
                </span>
              )}
              {member ? (
                <button
                  type="button"
                  onClick={() => {
                    setMoreOpen(false);
                    logout();
                  }}
                  className="whitespace-nowrap rounded-full border border-background-200 px-4 py-1.5 text-xs font-semibold text-foreground-700"
                >
                  로그아웃
                </button>
              ) : (
                <button
                  type="button"
                  onClick={() => {
                    setMoreOpen(false);
                    openLogin();
                  }}
                  className="whitespace-nowrap rounded-full bg-primary-500 px-4 py-1.5 text-xs font-semibold text-background-50"
                >
                  로그인
                </button>
              )}
            </div>

            <div className="grid grid-cols-4 gap-2">
              {moreItems.map((item) => (
                <NavLink
                  key={item.to}
                  to={item.to}
                  onClick={() => setMoreOpen(false)}
                  className="flex flex-col items-center gap-1.5 rounded-lg py-3 text-foreground-700 hover:bg-background-100"
                >
                  <i className={`${item.icon} text-2xl text-primary-600`} aria-hidden="true"></i>
                  <span className="text-[11px]">{item.label}</span>
                </NavLink>
              ))}
            </div>
          </div>
        </div>
      )}
    </>
  );
}